/* eslint-disable @typescript-eslint/ban-types */
import { isEmpty } from 'lodash';
import { Inject, Injectable } from '@angular/core';
import { ENV, environment, IEnvironment } from 'env/config';
import { forkJoin, Observable, of, combineLatest } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { Service } from '../entity';
import { DataService } from '../http/dataService';
import { Result } from '../interfaces/helpers';
import { CentralApis } from '../interfaces/internal';
import { Walklist, IWalklist } from '../models/walklist.model';
import { vendorManagement } from '../models/vendorManagement.queries';
import { Repository } from './repository';
import { IRepRosterSalesRep } from '../models/repRoster.model';
import { HttpHeaders } from '@angular/common/http';
import { ValueService } from './value';
import { IWalklistSequence } from '../map/feature/ifeature';

@Service({
  api: 'walklist',
  model: Walklist,
})
@Injectable({ providedIn: 'root' })
export class WalklistService extends Repository<Walklist> {
  private repRoster: Repository<IRepRosterSalesRep>;

  constructor(
    @Inject(ENV) env: IEnvironment,
    dataService: DataService,
    private picklistValueService: ValueService
  ) {
    super({
      client: dataService,
      url: (env.api.backend.central.endpoint as CentralApis).territory,
    });

    this.repRoster = new Repository<IRepRosterSalesRep>({
      client: dataService,
      url: env.api.backend.reproster.endpoint as string,
    });

    this.repRoster.api = '';
  }

  /**
   * @Method lazyLoadWalkListDetails
   * @Param queryParams: query Object
   * @Param progressQuery: progress query object
   * @Description:  Fetch walk list addresses.
   **/
  lazyLoadWalkListDetails(queryParams?: any) {
    const url = `${environment.api.backend.central.endpoint['activity']}address`;
    return this.dataService.getDataByLazyLoading(url, queryParams);
  }

  /**
   * @Method getWalkListDetails
   * @Param queryParams: query Object
   * @Description:  Fetch walk list details.
   **/
  getWalkListDetails<I>(
    queryParams: any,
    id?: string,
    headers?: HttpHeaders | { [p: string]: string | string[] }
  ): Observable<I> {
    const url = `${environment.api.backend.central.endpoint['territory']}walklist/${id}`;
    const hds = headers ?? this.headers;
    return this.dataService.get<I>(url, queryParams, hds).pipe(
      take(1),
      map((result: any) => {
        if (result.items) {
          return result.items.length !== 0 ? result.items[0] : {};
        } else if (Array.isArray(result)) {
          return result[0];
        } else {
          return result;
        }
      })
    );
  }

  /**
   * @Method getWalkListOverview
   * @Param queryParams: query Object
   * @Param progressQuery: progress query object
   * @Description:  Both are new query API's to fetch territory walk list and get progress report by territory ID.
   **/
  getWalkListOverview(queryParams?: any, id?: any): any {
    return combineLatest([
      this.getWalkListDetails({}, id),
      super.getWalkListProgress({ walklistIds: id }),
      this.picklistValueService.getAll({ picklistId: 36 }),
    ]).pipe(
      map(([walkList, progressReport, picklist]) => ({
        ...(walkList as object),
        pickList: picklist?.items || [],
        completion: this.calculateProgress(walkList, progressReport.items),
      }))
    );
  }

  /**
   * @Method fetchDataByLazyLoading
   * @Param queryParams: query Object
   * @Param progressQuery: progress query object
   * @Description:  Both are new query API's to fetch territory walk list and get progress report by territory ID.
   **/
  fetchDataByLazyLoading(queryParams?: any, progressQuery?: any, repsMap?: {}) {
    return combineLatest([
      super.loadDataLazily(queryParams),
      super.getWalkListProgress(progressQuery),
    ]).pipe(
      map(([walkList, progressReport]) => ({
        meta: walkList.meta,
        items: walkList.items.map((item) => ({
          ...item,
          vendorSalesRepName:
            !isEmpty(repsMap) && !isEmpty(item) && item.salesReps
              ? repsMap && repsMap[item.salesReps[0]]
                ? item.salesReps.map((rep) => repsMap[rep]?.text).join(', ')
                : null
              : null,
          completion: this.calculateProgress(item, progressReport.items),
        })),
      }))
    );
  }

  /**
   * @Method moveAddressToAnotherWalklist
   * @Param queryParams: query Object
   * @Description:  this is used to move addresses to a different walklist.
   **/
  moveAddressToAnotherWalklist(queryParams?: any): Observable<any> {
    const url = `${environment.api.backend.central.endpoint['territory']}walklist/addressSimplified`;

    return this.dataService.put<any>(url, queryParams, this.headers).pipe(
      take(1),
      map((result: any) => result)
    );
  }

  /**
   * @Method getAddressesByTerritory
   * @Param queryParams: query Object
   * @Description:  returns all the addresses Ids and walklist for a territory.
   **/
  getAddressesByTerritory(
    filter: string
  ): Observable<Result<IWalklistSequence>> {
    const endpoint = `${environment.api.backend.central.endpoint['territory']}walklistAddressSequence`;
    const url = `${endpoint}/query?${filter}`;

    // just to get all the address information we need to add this limit
    const queryParams = {
      limit: 10000,
    };

    return this.dataService
      .get<IWalklistSequence>(url, queryParams, this.headers)
      .pipe(
        take(1),
        map((result: any) => result)
      );
  }

  getAll(queryParams?: any, conditions?): Observable<Result<Walklist>> {
    if (conditions && conditions.condition.indexOf('vendorGroupsById') !== -1) {
      queryParams = {
        ...queryParams,
        vendorGroups: conditions.value?.toString(),
      };
    } else if (
      conditions &&
      conditions.condition.indexOf('vendorAccountName') !== -1
    ) {
      queryParams = {
        ...queryParams,
        vendorAccountNames: conditions.value?.toString(),
      };
    }

    return super.getAll<IWalklist>(queryParams).pipe(
      switchMap((walklistsResult) => {
        const emptyResult: Result<IRepRosterSalesRep> = {
          items: [],
          links: null,
          meta: null,
        };

        // getting unique IDs
        const createdById = walklistsResult.items.map(
          (walklist) => `'${walklist.createdBy}'`
        );
        const modifiedById = walklistsResult.items.map(
          (walklist) => `'${walklist.modifiedBy}'`
        );
        const cpmsId = walklistsResult.items.map(
          (walklist) => `'${walklist.salesReps[0] ?? ''}'`
        );

        const vendorSalesRepCpmsId = [
          ...new Set(cpmsId.concat(createdById).concat(modifiedById)),
        ];

        // creating query
        const cpmsIdQuery = {
          q: vendorManagement.vendorSalesRep.byCpmsId(
            vendorSalesRepCpmsId.join()
          ),
        };

        return forkJoin([
          of(walklistsResult),
          vendorSalesRepCpmsId.length === 0
            ? of(emptyResult)
            : this.repRoster.getAll<IRepRosterSalesRep>(cpmsIdQuery),
        ]);
      }),
      map((data) => {
        const walklistsResult = data[0];
        const vendorSalesReps = data[1].items;

        walklistsResult.items = walklistsResult.items.map((walklist) =>
          Walklist.createFromDTO(walklist, vendorSalesReps)
        );

        return walklistsResult;
      })
    );
  }

  createFromDTO(walklist: IWalklist): Walklist {
    return new Walklist(walklist);
  }

  /**
   * @Method calculateProgress
   * @Param walkListItem: Object
   * @Param progressData: progress list
   * @Description:  Calculating current progress for walk list completion column.
   **/
  calculateProgress(walkListItem, progressData?: any[]): string {
    let result = '0';
    if (!!progressData && progressData.length > 0) {
      progressData.map((progress) => {
        if (walkListItem.id === progress?.walklistid) {
          const percentage: string = (
            (+progress?.knockedstops / walkListItem.stops) *
            100
          ).toFixed(2);
          result = `${Math.round(+percentage)}`;
        }
      });
    }
    return result;
  }

  /**
   * @Method calculateOverAllProgress
   * @Param territory addresses list: Array
   * @Param progressData: knocked Addresses list: Array
   * @Description: Calculating over all territory progress.
   **/
  calculateOverAllProgress(addresses, overallKnocked?: any[]): number {
    let result = 0;
    if (addresses?.length > 0 && overallKnocked?.length === 1) {
      const percentage: string = (
        (+overallKnocked[0]?.knockedstops / addresses?.length) *
        100
      ).toFixed(2);
      result = +`${Math.round(+percentage)}`;
    }
    return result;
  }
}
